import { areRecordsEqual } from "../../utils/compareData";
import { forAssetDiff } from "../../utils/dealAssetUpdateSync";

// Serialized form of object-valued deal fields, keyed by reference. Deal state
// is updated immutably (`{ ...prev, [field]: value }`), so untouched fields keep
// their reference between keystrokes and only the edited field is re-serialized.
const jsonCache = new WeakMap<object, string>();

function toJson(value: object): string {
  let json = jsonCache.get(value);
  if (json === undefined) {
    json = JSON.stringify(value);
    jsonCache.set(value, json);
  }
  return json;
}

function isDealFieldEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a && b && typeof a === "object" && typeof b === "object") {
    return toJson(a) === toJson(b);
  }
  return false;
}

/**
 * Field-by-field equivalent of `JSON.stringify(deal) !== JSON.stringify(originalDeal)`
 * (a missing key and an `undefined` value are the same), without serializing the
 * whole deal on every render. Key order is not treated as a change.
 */
export function hasDealFieldChanges(
  deal: object,
  originalDeal: object,
): boolean {
  const current = deal as Record<string, unknown>;
  const original = originalDeal as Record<string, unknown>;
  const keys = new Set([...Object.keys(current), ...Object.keys(original)]);
  for (const key of keys) {
    if (!isDealFieldEqual(current[key], original[key])) return true;
  }
  return false;
}

type RelatedRecords = {
  assets: any[];
  originalAssets: any[];
  tenants: any[];
  originalTenants: any[];
  contacts: any[];
  originalContacts: any[];
};

/** Related tables: areRecordsEqual + forAssetDiff (Assets / saveUpdateDeal). */
export function hasRelatedRecordChanges(args: RelatedRecords): boolean {
  const {
    assets,
    originalAssets,
    tenants,
    originalTenants,
    contacts,
    originalContacts,
  } = args;

  if (
    !areRecordsEqual(forAssetDiff(assets), forAssetDiff(originalAssets))
  ) {
    return true;
  }

  if (!areRecordsEqual(tenants, originalTenants)) {
    return true;
  }

  if (!areRecordsEqual(contacts, originalContacts)) {
    return true;
  }

  return false;
}

/**
 * Edit-deal dirty check. Text fields commit on every keystroke, so callers
 * should memoize the two halves separately (see CreateDealUpdate) so typing
 * in a deal field does not re-compare the related tables.
 */
export function hasEditDealChanges(
  args: { deal: object; originalDeal: object } & RelatedRecords,
): boolean {
  const { deal, originalDeal, ...related } = args;
  return (
    hasDealFieldChanges(deal, originalDeal) || hasRelatedRecordChanges(related)
  );
}
